/* ============================================================
   Lab Effects — MOBILE BACK TRAIL
   assets/le-mobile-nav.js

   The top bar's chevron walks back along the pages the shopper
   actually visited:

       Home -> Kits -> CDT Kit
       chevron -> Kits
       chevron -> Home

   WHY A TRAIL RATHER THAN history.back()

   history.back() is right only when the shopper arrived by walking
   forward through the site. Most mobile sessions do not: they land
   on a product from Google, an ad or a shared link, where the
   previous history entry is the search results page — so the
   chevron would throw them off the site entirely. A trail we keep
   ourselves can only ever point at pages of ours they have seen,
   and falls back to Home when there is nothing behind them.

   Kept in sessionStorage, so it is per-tab and dies with the tab —
   a trail that outlived the session would send someone "back" to a
   page from yesterday.

   THE TRAIL ALSO CARRIES SCROLL POSITION

   Each entry is { u: url, y: scrollY }. Because the chevron navigates
   with `location.href` it is a FORWARD navigation as far as the
   browser is concerned, so nothing restores scroll for us and the
   shopper landed back at the top of the collection — after scrolling
   through a hundred products to find the one they opened. So we save
   where they were on the way out and put them back on the way in.

   This deliberately does NOT touch history.scrollRestoration. Real
   browser back/forward still restores natively, and better than we
   can; we only fill the gap our own navigation creates — and where
   the browser's own previous entry provably IS the page the trail
   wants (document.referrer matches), the chevron uses history.back()
   and lets the browser do all of it.
   ============================================================ */
(function () {
  'use strict';

  var KEY = 'le-nav-trail';
  var RESTORE_KEY = 'le-nav-restore';
  var MAX = 25;

  function read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      // Entries were plain URL strings before scroll memory existed. A tab open
      // across the deploy still holds those, so normalise rather than throw the
      // shopper's trail away.
      return arr.map(function (e) {
        return typeof e === 'string' ? { u: e, y: 0 } : { u: e.u, y: e.y || 0 };
      }).filter(function (e) { return !!e.u; });
    } catch (e) {
      // Private mode and blocked site data both throw here. A dead trail must
      // degrade to "chevron goes Home", never to a broken control.
      return [];
    }
  }

  function write(arr) {
    try { sessionStorage.setItem(KEY, JSON.stringify(arr.slice(-MAX))); } catch (e) {}
  }

  function here() { return location.pathname + location.search; }

  /* ---------- scroll memory ----------

     window.scrollY is not readable while a sheet is open: every one of the
     three lock mechanisms (see CLAUDE.md) makes it report 0 even though the
     shopper is scrolled down. Sampling it then would persist a 0 and send them
     to the top of the collection — the exact bug this is meant to fix — so a
     locked page simply keeps the last honest reading. */
  var currentY = 0;

  function scrollLocked() {
    return document.documentElement.classList.contains('le-sheet-locked') ||
           document.documentElement.hasAttribute('scroll-lock') ||
           document.body.style.position === 'fixed';
  }

  window.addEventListener('scroll', function () {
    if (!scrollLocked()) currentY = window.scrollY || window.pageYOffset || 0;
  }, { passive: true });

  // Stamp the live position onto THIS page's trail entry.
  function persist() {
    var t = read();
    if (!t.length || t[t.length - 1].u !== here()) return;
    if (!scrollLocked()) currentY = window.scrollY || window.pageYOffset || 0;
    t[t.length - 1].y = currentY;
    write(t);
  }

  /* pagehide, not beforeunload: iOS Safari fires beforeunload unreliably and
     not at all into the back-forward cache. visibilitychange covers the
     app-switch case, where the page may never unload at all. */
  window.addEventListener('pagehide', persist);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') persist();
  });

  /* Put the shopper back — in ONE synchronous jump, before the first paint.

     The previous version scrolled inside requestAnimationFrame and re-asserted
     each frame until the document grew tall enough to reach the target. That
     is what produced the visible "lands at the top, flashes, then jumps down":
     the browser painted at scroll 0, then we moved it, sometimes more than
     once as images landed.

     Two changes kill the flash:

       1. RESERVE THE HEIGHT FIRST. The scroll was being clamped because at
          this moment the document can still be shorter than the target, and
          scrollTo silently pins to the bottom. Setting a min-height of
          `target + one viewport` guarantees the position exists before we ask
          for it, so the very first scrollTo lands exactly right.
       2. DO IT SYNCHRONOUSLY. This script is deferred, so it runs after the
          document is parsed but before DOMContentLoaded — scrolling here,
          rather than a frame later, means the position is already correct when
          the page is painted.

     The reservation is released as soon as the real content is tall enough to
     hold the position on its own, measured on <body> (whose height is its own
     content and so is not inflated by the min-height we put on <html>). Any
     deliberate touch, wheel or key releases it immediately too: once the
     shopper has taken over, holding an artificial height is worse than
     landing short. */
  function restoreTo(y) {
    if (!(y > 0)) return;
    var doc = document.documentElement;
    var savedBehavior = doc.style.scrollBehavior;
    var savedMinHeight = doc.style.minHeight;
    var need = y + window.innerHeight;
    var released = false;

    function release() {
      if (released) return;
      released = true;
      doc.style.minHeight = savedMinHeight;
      doc.style.scrollBehavior = savedBehavior;
      currentY = window.scrollY || 0;
    }

    doc.style.scrollBehavior = 'auto';              // never animate the restore
    doc.style.minHeight = need + 'px';
    window.scrollTo(0, y);
    currentY = y;

    window.addEventListener('touchstart', release, { passive: true, once: true });
    window.addEventListener('wheel', release, { passive: true, once: true });
    window.addEventListener('keydown', release, { once: true });

    var deadline = Date.now() + 2000;
    function settle() {
      if (released) return;
      var content = document.body ? document.body.scrollHeight : 0;
      if (content >= need || Date.now() >= deadline) {
        release();
        // One last assert, in case dropping the reservation shortened the page.
        if (Math.abs((window.scrollY || 0) - y) > 1) {
          doc.style.scrollBehavior = 'auto';
          window.scrollTo(0, Math.min(y, Math.max(0, doc.scrollHeight - window.innerHeight)));
          doc.style.scrollBehavior = savedBehavior;
          currentY = window.scrollY || 0;
        }
        return;
      }
      requestAnimationFrame(settle);
    }
    requestAnimationFrame(settle);
  }

  /* A restore token is written by the chevron immediately before it navigates,
     and is consumed exactly once. It is cleared even when it does not match
     this page, so a token left behind by an abandoned navigation can never
     fire on some unrelated page later in the session. */
  function consumeRestore() {
    var token = null;
    try {
      var raw = sessionStorage.getItem(RESTORE_KEY);
      if (raw) token = JSON.parse(raw);
      sessionStorage.removeItem(RESTORE_KEY);
    } catch (e) { return; }
    if (token && token.u === here()) restoreTo(token.y || 0);
  }

  /* Record this page.

     The three cases matter, because the browser's own Back button moves the
     shopper without telling us:

       already at the top  -> a reload; leave the trail alone
       present further back -> they went back (ours or the browser's), so cut
                               the trail to that point instead of appending a
                               duplicate and building a loop
       not present          -> a genuine step forward; push it */
  function record() {
    var trail = read();
    var url = here();

    if (trail.length && trail[trail.length - 1].u === url) return trail;

    var at = -1;
    for (var i = trail.length - 1; i >= 0; i--) { if (trail[i].u === url) { at = i; break; } }
    if (at > -1) {
      trail = trail.slice(0, at + 1);
      write(trail);
      return trail;
    }

    trail.push({ u: url, y: 0 });
    write(trail);
    return trail;
  }

  function previous(trail) {
    return trail.length > 1 ? trail[trail.length - 2] : null;
  }

  function paint(trail) {
    var back = document.querySelector('[data-le-back]');
    if (!back) return;
    var prev = previous(trail);
    // Keep a real destination on the element either way: with no trail behind
    // them the chevron still means something, it means Home.
    back.setAttribute('href', prev ? prev.u : '/');
  }

  var trail = record();
  paint(trail);
  consumeRestore();

  document.addEventListener('click', function (e) {
    var back = e.target.closest('[data-le-back]');
    if (!back) return;

    var t = read();
    var prev = previous(t);
    if (!prev) return;            // href="/" already handles it — let it through

    e.preventDefault();
    /* Stamp where we are before leaving. pagehide fires for this navigation
       too, but only after the click handler returns, and the entry it would
       write is about to be popped — so record it here, on the entry that
       survives. */
    persist();
    t = read();
    prev = previous(t) || prev;
    // Drop the page being left, so the NEXT chevron goes one further back
    // rather than bouncing between the last two pages.
    t.pop();
    write(t);
    try {
      sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ u: prev.u, y: prev.y || 0 }));
    } catch (err) {}

    /* PREFER REAL HISTORY WHEN IT PROVABLY POINTS AT THE SAME PAGE.

       The trail exists because history.back() is wrong in general — someone
       who landed on a product from Google has the search results behind them,
       and back would throw them off the site. But when document.referrer IS
       the page the trail wants, the browser's previous entry and ours are the
       same entry, and back() is strictly better than a fresh navigation: the
       browser restores the scroll position itself, before paint, and may serve
       the page from the back-forward cache with no reload at all. That is the
       difference between arriving already in place and arriving at the top and
       being moved.

       Anything else — a mismatch, no referrer, a cross-origin one — falls
       through to the explicit navigation below, which restores manually. */
    var ref = (document.referrer || '').split('#')[0];
    var refPath = ref.indexOf(location.origin) === 0 ? ref.slice(location.origin.length) : null;
    if (refPath && refPath === prev.u && window.history && history.length > 1) {
      /* If back() does not actually move us — a replaced entry, a browser that
         declines — fall back rather than stranding the shopper on a dead
         chevron. pagehide means it worked, so cancel the failsafe there. */
      var failsafe = setTimeout(function () { location.href = prev.u; }, 600);
      window.addEventListener('pagehide', function () { clearTimeout(failsafe); }, { once: true });
      history.back();
      return;
    }

    location.href = prev.u;
  });

  // A bfcache restore re-runs no script, so re-sync the chevron on the way in.
  // The browser has already restored scroll itself in that case, so drop any
  // token rather than scrolling on top of it.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    paint(record());
    try { sessionStorage.removeItem(RESTORE_KEY); } catch (err) {}
    currentY = window.scrollY || 0;
  });
})();
